/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box } from "@chakra-ui/react";
import { CSSTransition } from "react-transition-group";
import ReceiverAccountSearchToolbar from "./components/ReceiverAccountSearchToolbar";
import ReceiverAccountSearchForm, {
  Filters,
  SearchFieldsMap,
} from "./components/ReceiverAccountSearchForm";
import ReceiverAccountSearchList from "./components/ReceiverAccountSearchList";

const rowPerPage = 10;

const initialFilters: Filters = {
  asset: "",
  key: "",
  value: "",
};

interface FetchPayload {
  page: number;
  limit: number;
  lastkey: string;
  subaccountId: string;
  filters: Filters;
}

const ReceiverAccountSearch = ({ userPermissions }: { userPermissions: any }) => {
  const [searchFields, setSearchFields] = useState<SearchFieldsMap>({});
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedSubaccount, setSelectedSubaccount] = useState<any>(null);
  const [filters, setFilters] = useState<Filters>(initialFilters);

  const [tableData, setTableData] = useState<any[] | null>(null);
  const [isFetchSubmitted, setIsFetchSubmitted] = useState<boolean>(false);

  // Cursor pagination state (same shape as views/admin/receiver/index.tsx)
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(rowPerPage);
  const [rowCount, setRowCount] = useState<number>(rowPerPage);

  // Snapshot of the last-submitted search context — used by pagination so a
  // user editing the form mid-page doesn't change which subaccount/asset/key
  // the cursor applies to.
  const submittedRef = useRef<{
    subaccountId: string;
    filters: Filters;
  } | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<number>(0);
  const resultNodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  // Fetch search-fields map once on mount. No data fetch here.
  useEffect(() => {
    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;

    const loadFields = async () => {
      try {
        const response = await axios.get(
          process.env.REACT_APP_API_URL +
            "/api/receiveraccount/search-fields?asset=all",
          { signal }
        );
        if (signal.aborted || requestIdRef.current !== currentRequestId) return;
        const payload = response.data?.payload || {};
        setSearchFields(payload);
      } catch (error: any) {
        if (
          error.name === "CanceledError" ||
          error.name === "AbortError"
        ) {
          return;
        }
        console.error("Error loading search fields", error);
      } finally {
        if (!signal.aborted && requestIdRef.current === currentRequestId) {
          setIsFilterLoading(false);
        }
      }
    };

    loadFields();
  }, []);

  const fetchResults = async (
    payload: FetchPayload,
    signal?: AbortSignal,
    requestId?: number
  ) => {
    try {
      const { filters: f, subaccountId, limit, lastkey, page: newPage } = payload;

      const body = {
        subaccount_id: subaccountId,
        asset: f.asset,
        account_data: { [f.key]: f.value.trim() },
        limit,
        lastkey,
        usertype: "a",
      };

      const userRole = localStorage.getItem("userRole")?.toLowerCase() || "superadmin";

      const response = await axios.post(
        process.env.REACT_APP_API_URL +
          `/api/${userRole}/receiveraccount/action/receiver-account-search`,
        body,
        { signal, timeout: 28000 }
      );

      if (signal?.aborted) return;
      if (requestId !== undefined && requestIdRef.current !== requestId) return;

      const newData: any[] = response.data?.payload || [];
      const newLastKey: string = response.data?.lastkey || "";

      setTableData(newData);
      setCurrentLastKey(newLastKey);

      // Fake rowCount so AgGridPagination enables/disables Next correctly.
      // - If we got a lastkey -> more pages exist -> totalPages = page + 2
      // - If no lastkey      -> this is the last page -> totalPages = page + 1
      if (newLastKey) {
        setRowCount((newPage + 1) * limit + limit);
        setLastKeys((prev) => ({ ...prev, [newPage]: newLastKey }));
      } else {
        setRowCount(newPage * limit + (newData.length || 0));
      }
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return;
      }
      if (requestId !== undefined && requestIdRef.current !== requestId) return;
      console.error("Error searching receiver accounts", error);
      setTableData([]);
      setCurrentLastKey("");
      setRowCount(0);
    }
  };

  const handleSearchSubmit = async () => {
    if (!selectedSubaccount?.id) return;
    if (!filters.asset || !filters.key) return;
    if (filters.value.trim().length < 3) return;

    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;

    setIsLoading(true);
    setIsFetchSubmitted(true);
    setPage(0);
    setLastKeys({});
    setCurrentLastKey("");

    submittedRef.current = {
      subaccountId: selectedSubaccount.id,
      filters: { ...filters, value: filters.value.trim() },
    };

    await fetchResults(
      {
        page: 0,
        limit: pageSize,
        lastkey: "",
        subaccountId: selectedSubaccount.id,
        filters: { ...filters, value: filters.value.trim() },
      },
      signal,
      currentRequestId
    );

    if (signal.aborted || requestIdRef.current !== currentRequestId) return;
    setIsLoading(false);
  };

  const handleSearchClear = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setFilters(initialFilters);
    setSelectedSubaccount(null);
    setTableData(null);
    setIsFetchSubmitted(false);
    setPage(0);
    setPageSize(rowPerPage);
    setLastKeys({});
    setCurrentLastKey("");
    setRowCount(rowPerPage);
    submittedRef.current = null;
  };

  const setPaginationModel = async (newValue: {
    page: number;
    pageSize: number;
  }) => {
    if (!submittedRef.current) return;
    const snapshot = submittedRef.current;

    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;

    setIsLoading(true);

    let nextPage = newValue.page;
    let nextLimit = newValue.pageSize;
    let nextLastKey = currentLastKey;

    // Going back to a previous page
    if (newValue.page < page) {
      nextLastKey = newValue.page !== 0 ? lastKeys[newValue.page - 1] || "" : "";
    }

    // Page-size change resets to page 0
    if (newValue.pageSize !== pageSize) {
      nextPage = 0;
      nextLastKey = "";
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys({});
    } else {
      setPage(newValue.page);
    }

    await fetchResults(
      {
        page: nextPage,
        limit: nextLimit,
        lastkey: nextLastKey,
        subaccountId: snapshot.subaccountId,
        filters: snapshot.filters,
      },
      signal,
      currentRequestId
    );

    if (signal.aborted || requestIdRef.current !== currentRequestId) return;
    setIsLoading(false);
  };

  const handleRefresh = async () => {
    if (!submittedRef.current) return;
    const snapshot = submittedRef.current;

    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;

    setIsLoading(true);
    setPage(0);
    setLastKeys({});
    setCurrentLastKey("");

    await fetchResults(
      {
        page: 0,
        limit: pageSize,
        lastkey: "",
        subaccountId: snapshot.subaccountId,
        filters: snapshot.filters,
      },
      signal,
      currentRequestId
    );

    if (signal.aborted || requestIdRef.current !== currentRequestId) return;
    setIsLoading(false);
  };

  return (
    <Box sx={{ position: "relative" }}>
      <ReceiverAccountSearchToolbar
        isLoading={isLoading || isFilterLoading}
        refreshDisabled={!isFetchSubmitted}
        onRefresh={handleRefresh}
      />

      <ReceiverAccountSearchForm
        isLoading={isLoading}
        isFilterLoading={isFilterLoading}
        searchFields={searchFields}
        filters={filters}
        setFilters={setFilters}
        selectedSubaccount={selectedSubaccount}
        setSelectedSubaccount={setSelectedSubaccount}
        handleSearchSubmit={handleSearchSubmit}
        handleSearchClear={handleSearchClear}
      />

      <CSSTransition
        in={isFetchSubmitted && tableData !== null}
        timeout={300}
        classNames="slide-down"
        unmountOnExit
        nodeRef={resultNodeRef}
      >
        <div ref={resultNodeRef}>
          {isFetchSubmitted && tableData && (
            <ReceiverAccountSearchList
              isLoading={isLoading}
              tableData={tableData}
              page={page}
              pageSize={pageSize}
              rowCount={rowCount}
              setPaginationModel={setPaginationModel}
              currentLastKey={currentLastKey}
            />
          )}
        </div>
      </CSSTransition>
    </Box>
  );
};

export default ReceiverAccountSearch;
